import React from 'react';
import DOMPurify from 'dompurify';
import { LeftOutlined, RightOutlined, AlignLeftOutlined, PlayCircleOutlined, FunctionOutlined } from '@ant-design/icons';
import { Container, QuestionStyle } from 'pages/new-questions/styles/common';
import { QuestionResolutionModals } from '../QuestionResolutionModals';
import {
  CaseStudyContent,
  SplitLayout,
  ContextColumn,
  QuestionColumn,
  QuestionScrollArea,
  StepCode,
  StepFooter,
  FooterCenter,
  NavButton,
  ConfirmButton,
} from './styles';
import { CaseStudyQuestionProps } from './utils/types';
import { useCaseStudyQuestionState } from './hooks/useCaseStudyQuestionState';
import { QuestionHeader } from './components';
import { AnswerList } from '../ObjectiveQuestion/components';
import { ObjectiveAnswer } from '../../types';
import arrowSendContext from 'img/arrow-send-context.svg';
import contextQuestionsIcon from 'img/context-questions-icon.svg';

// Converte Answer (step_multiple) → ObjectiveAnswer (AnswerList)
// selectedAnswerId é necessário para gerar answereds.is_marked, que o AnswerList usa
// para decidir se exibe o botão "Não sei" como selecionado/errado.
const mapToObjectiveAnswers = (
  answers: { answer_id: number; answer: string; correct: number; order: number; score: string }[],
  selectedAnswerId?: number,
): ObjectiveAnswer[] =>
  answers.map((a) => ({
    id_resposta: a.answer_id,
    id_questao: 0,
    question_step_id: null,
    correta: a.correct,
    resposta: a.answer,
    img_resposta: null,
    created_at: null,
    updated_at: null,
    deleted_at: null,
    order: a.order,
    score: a.score,
    answereds:
      selectedAnswerId !== undefined
        ? [{ is_marked: a.answer_id === selectedAnswerId, is_correct: a.correct === 1 }]
        : undefined,
  }));

const CaseStudyQuestion: React.FC<CaseStudyQuestionProps> = ({
  question,
  isTest = false,
  resolved = false,
  externalStepAnswers,
  externalStepIndex,
  initialStepIndex,
  onStepAnswerChange,
  BackButton,
  Header,
  questionNumber,
  totalQuestions,
}) => {
  const {
    state,
    currentStep,
    effectiveStepIndex,
    totalSteps,
    allStepsAnswered,
    isAlreadySubmitted,
    getSelectedAnswerId,
    selectAnswer,
    handleSubmitAll,
    goToPrev,
    goToNext,
    setQuestionTextShow,
    setQuestionVideoStart,
    setModalVideoOption,
    setLSBVideo,
    setFormulaModalShow,
  } = useCaseStudyQuestionState({
    question,
    isTest,
    resolved,
    externalStepAnswers,
    externalStepIndex,
    initialStepIndex,
    onStepAnswerChange,
  });

  if (!currentStep) return null;

  const stepId = currentStep.id;
  const selectedAnswerId = getSelectedAnswerId(stepId);
  const correctAnswerId = state.stepAnswersCorrect[stepId];
  const mappedAnswers = mapToObjectiveAnswers(currentStep.answers, selectedAnswerId);

  const isLastStep = effectiveStepIndex === totalSteps - 1;
  const isFirstStep = effectiveStepIndex === 0;

  // Exibe "Salvar e ver resultado" somente quando TODOS os steps têm resposta e ainda não foi enviado
  const showConfirmButton = !isTest && !resolved && allStepsAnswered && !isAlreadySubmitted;
  // Após submissão global ou em modo revisão, as alternativas ficam somente leitura
  const isReadOnly = resolved || isAlreadySubmitted;

  return (
    <Container isTest={isTest}>
      <CaseStudyContent isTest={isTest}>
        {Header}
        <QuestionHeader
          questionNumber={questionNumber}
          totalQuestions={totalQuestions}
          currentStep={effectiveStepIndex + 1}
          totalSteps={totalSteps}
          questionAnswer={selectedAnswerId}
          questionAnswerCorrect={correctAnswerId}
        />

        {/* O código da questão não é exibido em tela: o que identifica a questão
            respondida é o código do step, mostrado ao lado do enunciado. */}
        <SplitLayout $isTest={isTest}>
          {/* Coluna esquerda: contexto do caso */}
          <ContextColumn
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(question.case_context || '') }}
          />

          {/* Coluna direita: questão atual + alternativas */}
          <QuestionColumn>
            <QuestionScrollArea>
              {currentStep.code && (
                <StepCode>
                  <img src={contextQuestionsIcon} alt="" />
                  <p>Caso de estudo ({currentStep.code})</p>
                </StepCode>
              )}
              <QuestionStyle>
                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentStep.statement || '') }} />
              </QuestionStyle>
              <AnswerList
                answers={mappedAnswers}
                questionAnswer={selectedAnswerId}
                questionAnswerCorrect={correctAnswerId}
                onSelectAnswer={isReadOnly ? () => {} : selectAnswer}
                resolved={isAlreadySubmitted || resolved}
              />
            </QuestionScrollArea>
          </QuestionColumn>
        </SplitLayout>

        {/* Rodapé standalone: navegação entre steps + salvar */}
        {!isTest && (
          <StepFooter>
            {/* Esquerda: Anterior */}
            <NavButton type="button" onClick={goToPrev} disabled={isFirstStep}>
              <LeftOutlined /> Anterior
            </NavButton>

            {/* Centro: botões de resolução por step (após submissão ou revisão) */}
            <FooterCenter>
              {(isAlreadySubmitted || resolved) && currentStep.questao_comentada && (
                <NavButton type="button" onClick={() => setQuestionTextShow(true)}>
                  <AlignLeftOutlined /> Ler Resolução
                </NavButton>
              )}
              {(isAlreadySubmitted || resolved) && currentStep.video_embed && (
                <NavButton
                  type="button"
                  onClick={() =>
                    currentStep.video_embed_libras
                      ? setModalVideoOption(true)
                      : setQuestionVideoStart(true)
                  }
                >
                  <PlayCircleOutlined /> Assistir Resolução
                </NavButton>
              )}
              {(isAlreadySubmitted || resolved) && currentStep.formula && (
                <NavButton type="button" onClick={() => setFormulaModalShow(true)}>
                  <FunctionOutlined /> Fórmula
                </NavButton>
              )}
            </FooterCenter>

            {/* Direita: Salvar e ver resultado ou Próximo */}
            {showConfirmButton ? (
              <ConfirmButton onClick={handleSubmitAll}>
                Salvar e ver resultado
                <img src={arrowSendContext} alt="Enviar" />
              </ConfirmButton>
            ) : (
              <NavButton type="button" onClick={goToNext} disabled={isLastStep} $variant="primary">
                Próximo <RightOutlined />
              </NavButton>
            )}
          </StepFooter>
        )}

        {/* Rodapé prova: apenas botões de resolução (navegação fica no LecturesFooterPanel) */}
        {isTest && (isAlreadySubmitted || resolved) && (
          <StepFooter>
            <FooterCenter>
              {currentStep.questao_comentada && (
                <NavButton type="button" onClick={() => setQuestionTextShow(true)}>
                  <AlignLeftOutlined /> Ler Resolução
                </NavButton>
              )}
              {currentStep.video_embed && (
                <NavButton
                  type="button"
                  onClick={() =>
                    currentStep.video_embed_libras
                      ? setModalVideoOption(true)
                      : setQuestionVideoStart(true)
                  }
                >
                  <PlayCircleOutlined /> Assistir Resolução
                </NavButton>
              )}
              {currentStep.formula && (
                <NavButton type="button" onClick={() => setFormulaModalShow(true)}>
                  <FunctionOutlined /> Fórmula
                </NavButton>
              )}
            </FooterCenter>
          </StepFooter>
        )}

        <QuestionResolutionModals
          modalVideoOption={state.modalVideoOption}
          setModalVideoOption={setModalVideoOption}
          setIsLibrasVideo={setLSBVideo}
          questionVideoStart={state.questionVideoStart}
          setQuestionVideoStart={setQuestionVideoStart}
          isLibrasVideo={state.LSBVideo}
          videoEmbed={currentStep.video_embed}
          videoEmbedLibras={currentStep.video_embed_libras}
          questionTextShow={state.questionTextShow}
          setQuestionTextShow={setQuestionTextShow}
          questaoComentada={currentStep.questao_comentada}
          formulaModalShow={state.formulaModalShow}
          setFormulaModalShow={setFormulaModalShow}
          formula={currentStep.formula}
          formulaImagem={currentStep.formula_imagem}
        />
      </CaseStudyContent>
    </Container>
  );
};

export default CaseStudyQuestion;
